import clsx from 'clsx'
import { HYBRID_BENEFIT_LABEL, SECURITY_TYPE_LABEL } from '@/data/dedicatedServers'
import {
  DB_COST_LICENSING_NOTE,
  DB_RECOMMENDED_NOTE,
  RDS_POOL_LABEL,
  getPoolTier,
  memoryText,
  resolveVm,
  sessionsText,
  vcpuText,
  type PoolTierId,
} from '@/data/sizeCatalog'
import type { VmSizeCheck } from '@/types/infrastructure'
import { sizeOfTier, sizeText } from '@/utils/vmSizes'
import page from '@/pages/NewDeploymentPage.module.css'

interface SizesYouChoseProps {
  poolTier: PoolTierId
  /** True when a new SQL VM is built, which adds the DB VM. */
  newSqlVm: boolean
  /** True when a new RDS server is built, which has the golden image, Trusted launch and Azure Hybrid Benefit. */
  newRdsServer?: boolean
  /** The VM size each new server is built with in the chosen region. A row shows that SKU, which is not the catalog's where it was replaced. */
  vmSizes?: readonly VmSizeCheck[]
}

/**
 * The summary of every size the deployment is built with, for the Review step: the pool tier the user chose (the only choice), the RDS VM
 * it is built on, and, when a new SQL VM is built, the DB VM, which is shown as a recommended size that is not confirmed.
 */
export function SizesYouChose({ poolTier, newSqlVm, newRdsServer = true, vmSizes }: SizesYouChoseProps) {
  const tier = getPoolTier(poolTier)
  const rds = resolveVm('rds', poolTier)
  const db = resolveVm('db', poolTier)
  const rdsCheck = sizeOfTier(vmSizes, 'rds')
  const dbCheck = sizeOfTier(vmSizes, 'db')
  const rows: { label: string; value: string; mono?: boolean }[] = [
    { label: 'RDS pool tier', value: `${tier.label} · ${sessionsText(tier.sessions)}` },
    { label: 'Pool', value: RDS_POOL_LABEL },
    {
      label: 'RDS VM',
      value: `${rdsCheck ? sizeText(rdsCheck) : rds.sku} · ${vcpuText(rds)} · ${memoryText(rds)} · ${rds.disk}`,
      mono: true,
    },
    ...(newRdsServer
      ? [
          { label: 'Security type', value: SECURITY_TYPE_LABEL },
          { label: HYBRID_BENEFIT_LABEL, value: 'Enabled on the RDS VM' },
        ]
      : []),
    ...(newSqlVm
      ? [
          {
            label: 'DB VM',
            value: `${dbCheck ? sizeText(dbCheck) : db.sku} · ${vcpuText(db)} · ${memoryText(db)} · ${db.disk} · ${DB_RECOMMENDED_NOTE} · ${DB_COST_LICENSING_NOTE.replace('Est. cost', 'cost')}`,
            mono: true,
          },
        ]
      : []),
  ]
  return (
    <section aria-label="Sizes you chose" data-testid="sizes-you-chose">
      <h4 className={page.reviewSectionTitle}>Sizes you chose</h4>
      <div className={page.reviewTable}>
        {rows.map((row) => (
          <div key={row.label} className={page.reviewRow}>
            <span className={page.reviewLabel}>{row.label}</span>
            <span className={clsx(page.reviewValue, row.mono && page.reviewValueMono)}>{row.value}</span>
          </div>
        ))}
      </div>
    </section>
  )
}
